import {
  type ZoneFeatures,
  type SmarterSearchCluster,
  SMARTER_SEARCH_CLUSTERS,
} from 'src/config/types';
import type { MobileDevices } from '@seek/chalice-types';
import { isATargetDevice } from 'src/modules/app-install-banner';
import type {
  ChaliceStore,
  TypedAction,
  TypedThunkAction,
} from 'src/store/reducer';
import {
  type TSeekExperiments,
  toggleExperiment,
} from '../experiments/experimentHelpers';

const UPDATE_FEATURE_FLAGS = 'UPDATE_FEATURE_FLAGS';

export const updateFeatureFlags =
  (
    cookies: Record<string, string> = {},
    experiments: TSeekExperiments,
    {
      NORTHSTAR_SEO_TAGS,
      SPLITVIEW_ENABLED,
      SHOW_HOMEPAGE_BANNER,
      SHOW_FLOATING_SAVE_SEARCH,
      SHOULD_OPEN_APPLY_IN_NEW_TAB,
      AUTO_SELECT_SPLIT_VIEW_FIRST_JOB,
      SHOW_STICKY_APPLY_ON_DESKTOP,
      SHOW_MARKETING_AD_SPLIT_VIEW,
      SALARY_TRANSPARENCY_ENABLED,
    }: ZoneFeatures,
  ): TypedThunkAction =>
  (dispatch) => {
    const devices: MobileDevices[] = ['iOS', 'Android'];
    const SPLIT_VIEW_COOKIE =
      cookies.SRP_SPLIT_VIEW_1 || cookies.SRP_SPLIT_VIEW_ASIA_1;

    return dispatch({
      type: UPDATE_FEATURE_FLAGS,
      payload: {
        dynamicFlags: {
          salaryTransparency:
            SALARY_TRANSPARENCY_ENABLED ||
            toggleExperiment({
              experimentId: 'salaryTransparency',
              experiments,
              cookieValue: cookies.SALARY_TRANSPARENCY_ENABLED,
            }),
          branchBannerPreview: Boolean(cookies.BRANCH_BANNER_PREVIEW),
          isBranchEnabledFlag: isATargetDevice(devices),
          splitView:
            SPLITVIEW_ENABLED ||
            toggleExperiment({
              experimentId: 'splitView',
              experiments,
              cookieValue: SPLIT_VIEW_COOKIE,
            }),
          unifiedSrpSplitView1:
            toggleExperiment({
              experimentId: 'unifiedSrpSplitView1',
              experiments,
              cookieValue: SPLIT_VIEW_COOKIE,
            }) ||
            toggleExperiment({
              experimentId: 'splitView',
              experiments,
              cookieValue: SPLIT_VIEW_COOKIE,
            }),
          retireDisplayAds: cookies.RETIRE_DISPLAY_ADS,
          isApolloJDVEnabled: cookies.ENABLE_APOLLO_JDV,
          smarterSearchLaunchCluster: cookies.SMARTER_SEARCH_LAUNCH_CLUSTER,
          seoMetaTags: cookies.SEO_META_TAGS || Boolean(NORTHSTAR_SEO_TAGS),
          displayCandidateOnboardingBanner:
            cookies.DISPLAY_CANDIDATE_ONBOARDING_BANNER_OVERRIDE,
          showHomepageBanner: Boolean(
            cookies.SHOW_HOMEPAGE_BANNER || SHOW_HOMEPAGE_BANNER,
          ),
          showFloatingSaveSearch: Boolean(
            cookies.SHOW_FLOATING_SAVE_SEARCH || SHOW_FLOATING_SAVE_SEARCH,
          ),
          shouldOpenApplyInNewTab:
            Boolean(cookies.SHOULD_OPEN_APPLY_IN_NEW_TAB) ||
            SHOULD_OPEN_APPLY_IN_NEW_TAB,
          autoSelectSplitViewFirstJob:
            Boolean(cookies.AUTO_SELECT_SPLIT_VIEW_FIRST_JOB) ||
            AUTO_SELECT_SPLIT_VIEW_FIRST_JOB,
          showStickyApplyOnDesktop:
            Boolean(cookies.SHOW_STICKY_APPLY_ON_DESKTOP) ||
            SHOW_STICKY_APPLY_ON_DESKTOP,
          showMarketingAdSplitView: Boolean(
            cookies.SHOW_MARKETING_AD_SPLIT_VIEW ||
              SHOW_MARKETING_AD_SPLIT_VIEW,
          ),
        },
      },
    });
  };

export interface FeatureFlagsState {
  salaryTransparency: boolean;
  branchBannerPreview: boolean;
  isBranchEnabledFlag: boolean;
  splitView: boolean;
  unifiedSrpSplitView1: boolean;
  retireDisplayAds: boolean;
  isApolloJDVEnabled: boolean;
  smarterSearchLaunchCluster: SmarterSearchCluster;
  seoMetaTags: boolean;
  displayCandidateOnboardingBanner: boolean;
  showHomepageBanner: boolean;
  showFloatingSaveSearch: boolean;
  shouldOpenApplyInNewTab: boolean;
  autoSelectSplitViewFirstJob: boolean;
  showStickyApplyOnDesktop: boolean;
  showMarketingAdSplitView: boolean;
}

export const initialState: FeatureFlagsState = {
  salaryTransparency: false,
  branchBannerPreview: false,
  isBranchEnabledFlag: false,
  splitView: false,
  unifiedSrpSplitView1: false,
  retireDisplayAds: false,
  isApolloJDVEnabled: false,
  smarterSearchLaunchCluster: SMARTER_SEARCH_CLUSTERS[0],
  seoMetaTags: false,
  displayCandidateOnboardingBanner: false,
  showHomepageBanner: false,
  showFloatingSaveSearch: true,
  shouldOpenApplyInNewTab: false,
  autoSelectSplitViewFirstJob: true,
  showStickyApplyOnDesktop: false,
  showMarketingAdSplitView: false,
};

export type TFeatureFlagNames = keyof FeatureFlagsState;

export interface Action {
  type: typeof UPDATE_FEATURE_FLAGS;
  payload: {
    dynamicFlags: Record<string, any>;
  };
}

export default function reducer(
  state = initialState,
  action: TypedAction,
): FeatureFlagsState {
  switch (action.type) {
    case UPDATE_FEATURE_FLAGS: {
      const { dynamicFlags } = action.payload;
      return {
        ...state,
        ...dynamicFlags,
      };
    }

    default:
      return state;
  }
}

export const SPLIT_VIEW_FEATURE: TFeatureFlagNames = 'splitView';
export const UNIFIED_SPLIT_VIEW_TEST_1: TFeatureFlagNames =
  'unifiedSrpSplitView1';
export const SHOW_HOMEPAGE_BANNER_FEATURE: TFeatureFlagNames =
  'showHomepageBanner';
export const AUTO_SELECT_SPLIT_VIEW_FIRST_JOB = 'autoSelectSplitViewFirstJob';
export const SHOW_FLOATING_SAVE_SEARCH_FEATURE: TFeatureFlagNames =
  'showFloatingSaveSearch';
export const SHOW_STICKY_APPLY_ON_DESKTOP: TFeatureFlagNames =
  'showStickyApplyOnDesktop';

export const SHOW_MARKETING_AD_SPLIT_VIEW_FEATURE: TFeatureFlagNames =
  'showMarketingAdSplitView';

// defaults to 'default' cluster if no cookie
export const selectSmarterSearchCluster = (
  state: ChaliceStore,
): SmarterSearchCluster =>
  state.featureFlags?.smarterSearchLaunchCluster ?? SMARTER_SEARCH_CLUSTERS[0];

export const selectFeatureFlag =
  (flagName: TFeatureFlagNames) => (state: ChaliceStore) =>
    Boolean(state.featureFlags[flagName]);
